import { isOnPrem } from '@/utils/buildProfile';
import { isShareModalOpenAtom } from '@/atoms/chatAtoms';
import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import {
  sidebarPanelContentAtom,
  sidebarPanelPinnedAtom,
} from '@/atoms/sidebarPanelAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { ChatMode } from '@/components/chat/types/types';
import {
  IS_ASK_HEIDI_HISTORY_ENABLED,
  IS_AUTO_MODE_ONLY_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_EVIDENCE_LIBRARY_ENABLED,
  IS_EVIDENCE_USER_DOCS_ENABLED,
  IS_INSET_DESIGN_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { useCanShareEvidenceChat } from '@/components/chat/hooks/useCanShareEvidenceChat';
import {
  useIsAgentHomeV1Enabled,
  useIsMidChatClinicalLinkPersistEnabled,
  useIsProjectsEnabled,
  useIsShareEvidenceChatEnabled,
} from '@/features/flags';
import { useDeleteChatWithHomeFallback } from '@/hooks/useDeleteChatWithHomeFallback';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useSavedChats } from '@/hooks/useSavedChats';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AlignJustify, PanelLeftOpen } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ChatOverflowMenu,
  CustomizeEvidenceButton,
  LeftHeaderSpotlightWrapper,
  NonChatRouteActions,
  ShareChatButton,
} from './ChatHeaderActions';
import { ChatHeaderModals } from './ChatHeaderModals';
import { EvidenceUpgradeCtaButton } from './evidence-upgrade-cta-button';
import { SessionChatHistoryDropdown } from './SessionChatHistoryDropdown';
import { ShareChatOnboardingTooltip } from './ShareChatOnboardingTooltip';
import { useChatHeaderActions } from './useChatHeaderActions';

interface ChatHeaderProps {
  onClose: () => void;
  showMobileMenu?: boolean;
  mode: ChatMode;
  sessionId?: string | null;
  currentChatId?: string | null;
  onNewChat?: () => void;
  onSelectChat?: (chatId: string) => void;
  isStreaming?: boolean;
  /** Whether the current chat is empty (no messages). Used to disable "New chat" button. */
  isChatEmpty?: boolean;
  /** Chat title from parent (passed from useChatBackend via chatMeta) */
  chatTitle?: string;
  /** Persisted session link from GET chat (for share gate). EVI-2279 */
  backendSessionId?: string | null;
  /** Persisted patient link from GET chat (for share gate). EVI-2279 */
  backendPatientProfileId?: string | null;
  /** Whether the chat is rendered as an embeddable widget */
  isWidget?: boolean;
  /**
   * Off-session chats are titled Evidence. The inbox document bar sets this
   * so a legacy chat on that page is titled Ask Heidi.
   */
  preferModeTitle?: boolean;
}

export const ChatHeader = ({
  onClose,
  showMobileMenu = false,
  mode,
  sessionId,
  currentChatId,
  onNewChat,
  onSelectChat,
  isStreaming = false,
  isChatEmpty = true,
  chatTitle,
  backendSessionId,
  backendPatientProfileId,
  isWidget = false,
  preferModeTitle = false,
}: ChatHeaderProps): JSX.Element => {
  const { t } = useTranslations();
  const intl = useIntl();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useIsAuthenticated();
  const isOnChatRoute = router.pathname.includes('/chat');
  const isOnInAppChatRoute = router.pathname.includes('/scribe/chat');
  const isOnSessionRoute = router.pathname.startsWith('/scribe/session');
  const isLegacySidebarOpen = useAtomValue(chatSidebarOpenAtom);
  const sidebarPanelContent = useAtomValue(sidebarPanelContentAtom);
  const isSidebarPanelPinned = useAtomValue(sidebarPanelPinnedAtom);
  const setSidebarPanelContent = useSetAtom(sidebarPanelContentAtom);
  const [isShareModalOpen, setIsShareModalOpen] = useAtom(isShareModalOpenAtom);

  const isShareEvidenceChatEnabled = useIsShareEvidenceChatEnabled();
  const isMidChatClinicalLinkPersistEnabled = useIsMidChatClinicalLinkPersistEnabled();
  const isWorkEnabled = useIsProjectsEnabled();
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  const usesSidebarPanel =
    isOnInAppChatRoute &&
    IS_SIDEBAR_PANEL_ENABLED() &&
    !isAgentHomeV1Enabled;
  const hasMountedSidebarConsumer =
    !isOnInAppChatRoute || !isAgentHomeV1Enabled;
  const isSidebarOpen = usesSidebarPanel
    ? isSidebarPanelPinned || sidebarPanelContent !== null
    : hasMountedSidebarConsumer && isLegacySidebarOpen;
  const openSidebarPanel = useCallback(
    () => setSidebarPanelContent('evidence'),
    [setSidebarPanelContent]
  );
  const canShareEvidenceChat = useCanShareEvidenceChat({
    chatId: currentChatId,
    backendSessionId,
    backendPatientProfileId,
    isShareEnabled: isShareEvidenceChatEnabled,
    isMidChatPersistEnabled: isMidChatClinicalLinkPersistEnabled,
  });
  const showShareButton =
    isOnChatRoute && currentChatId && isAuthenticated && canShareEvidenceChat;
  const { isInFreeTrial: isInEvidenceFreeTrial, trialDaysRemaining: evidenceTrialDaysRemaining, hasTrialExpired: evidenceTrialExpired} = useEvidenceBillingInfo();
  const { isClinicianTrialUser, isClinicianPracticeTrialUser, isEligibleForPracticeTrial } = useReverseTrial();
  const { isChatSaved, toggleSaveChat } = useSavedChats();
  const { trackChatSaved, trackChatUnsaved } = useEvidenceAnalytics();
  const { deleteChatWithHomeFallback, isDeleting: isDeletingChat } =
    useDeleteChatWithHomeFallback();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    userDocsModalOpen,
    setUserDocsModalOpen,
    showSignUpModal,
    setShowSignUpModal,
    hasEvidencePro,
    handleClose,
    handleMenuClick,
    handleNewChat,
    handlePanelClick,
    handleCustomizeEvidenceClick,
    // The Projects flag is resolved HERE, where this file already resolves flags,
    // and crosses as a boolean. That is what lets `useChatHeaderActions` ask the
    // agents contracts door for the home path instead of naming a
    // `src/features/projects` module — one fewer outbound coupling for the
    // quarantine, and no new one, because this pair already existed.
  } = useChatHeaderActions({
    onClose,
    mode,
    canUseWorkHome: isWorkEnabled,
    onOpenPanel: usesSidebarPanel ? openSidebarPanel : undefined,
  });

  const isLibraryEnabled = IS_EVIDENCE_LIBRARY_ENABLED();
  // The in-app SidebarPanel consumes `sidebarPanelContentAtom`; the legacy and
  // standalone ChatSidebar consumes `chatSidebarOpenAtom`. Agent Home replaces
  // both on the in-app route, so it must not expose either sidebar opener.
  const showPanelButton =
    !isWidget &&
    !isMobile &&
    isOnChatRoute &&
    hasMountedSidebarConsumer &&
    !isSidebarOpen;

  // Legacy: only show old customize button if Library is disabled
  const showUserDocsButton =
    !isLibraryEnabled &&
    IS_EVIDENCE_USER_DOCS_ENABLED() &&
    isAuthenticated &&
    isOnChatRoute &&
    !isMobile;

  // ON-PREM-GATE: an appliance is deployment-licensed. Its user carries no
  // `evidence_billing`, so the trial terms read as "unpaid", and the
  // launch-trial flag is not pinned in the on-prem bundle — the trial/upgrade
  // CTA would open a modal that is not mounted there (F6). Same term
  // `use-upgrade-cta.ts` carries.
  const showTrialDaysCountdownButton =
    !isOnPrem &&
    isOnChatRoute &&
    isAuthenticated &&
    ((isInEvidenceFreeTrial && evidenceTrialDaysRemaining > 0) || evidenceTrialExpired || isClinicianTrialUser || isClinicianPracticeTrialUser || isEligibleForPracticeTrial) &&
    !isMobile &&
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

  // Use chatTitle prop (passed from parent via chatMeta) when auto-mode-only is enabled
  const headerTitle = IS_AUTO_MODE_ONLY_ENABLED() && chatTitle
    ? chatTitle
    : preferModeTitle && mode === ChatMode.LEGACY
      ? t('chat.header.askHeidi')
      : !isOnSessionRoute
        ? t('chat.header.evidence')
        : mode === ChatMode.LEGACY
          ? t('chat.header.askHeidi')
          : mode === ChatMode.AUTO
            ? t('chat.header.auto')
            : t('chat.header.evidence');

  const handleSaveToggle = useCallback(() => {
    if (!currentChatId) return;
    const wasSaved = isChatSaved(currentChatId);
    toggleSaveChat(currentChatId);
    if (wasSaved) {
      trackChatUnsaved(currentChatId, 'header');
    } else {
      trackChatSaved(currentChatId, 'header');
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Chat pinned',
          id: 'LgtNYPlCOI',
          description: 'Success toast after pinning an Evidence chat',
        }),
        position: 'top-right',
        className: 'w-fit',
      });
    }
  }, [
    currentChatId,
    intl,
    isChatSaved,
    toggleSaveChat,
    trackChatSaved,
    trackChatUnsaved,
  ]);

  const showSaveButton =
    isOnChatRoute && isAuthenticated && !isMobile && currentChatId && !isChatEmpty;

  const showDeleteButton =
    isOnChatRoute &&
    isAuthenticated &&
    !isMobile &&
    !!currentChatId &&
    isAgentHomeV1Enabled;

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!currentChatId) return;
    void deleteChatWithHomeFallback(currentChatId).then((succeeded) => {
      if (succeeded) {
        setIsDeleteDialogOpen(false);
      }
    });
  }, [currentChatId, deleteChatWithHomeFallback]);

  // On mobile full-screen evidence, the header actions are handled by MobileAppHeader
  const isOnFullScreenEvidence = isOnChatRoute && isMobile && isAuthenticated;

  if (isOnFullScreenEvidence || isMobile) {
    return (
      <ChatHeaderModals
        isAuthenticated={isAuthenticated}
        showSignUpModal={showSignUpModal}
        setShowSignUpModal={setShowSignUpModal}
        showUserDocsButton={!!showUserDocsButton}
        userDocsModalOpen={userDocsModalOpen}
        setUserDocsModalOpen={setUserDocsModalOpen}
        showShareButton={!!showShareButton}
        currentChatId={currentChatId}
        isShareModalOpen={isShareModalOpen}
        setIsShareModalOpen={setIsShareModalOpen}
        chatTitle={chatTitle}
        isStreaming={isStreaming}
        isDeleteDialogOpen={isDeleteDialogOpen}
        isDeletingChat={isDeletingChat}
        onCancelDelete={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
      />
    );
  }

  return (
    <div className={isOnChatRoute ? 'relative w-full' : 'relative px-5'}>
      <div
        className={
          isOnChatRoute
            ? 'flex w-full items-center justify-between px-6 py-3'
            : 'flex min-h-16 items-center justify-between py-4'
        }
      >
        <LeftHeaderSpotlightWrapper enabled={isOnSessionRoute}>
          <div
            id="left-header-content"
            className={cn(
              'flex flex-row items-center',
              isOnChatRoute ? 'gap-1' : 'gap-2'
            )}
          >
            {showMobileMenu && (
              <Button
                iconOnly
                variant="ghost"
                size="sm"
                onClick={handleMenuClick}
                aria-label={t('chat.header.openMenu')}
              >
                <AlignJustify size={20} />
              </Button>
            )}
            {showPanelButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    flush={isOnChatRoute ? 'start' : undefined}
                    onClick={handlePanelClick}
                    aria-label={t('chat.openEvidenceSidebar')}
                    data-testid="chat-panel-button"
                  >
                    <PanelLeftOpen />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {t('chat.openEvidenceSidebar')}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            )}
            {!(isAuthenticated && isOnChatRoute) && (
              <>
                {isOnSessionRoute &&
                sessionId &&
                isAuthenticated &&
                IS_ASK_HEIDI_HISTORY_ENABLED() ? (
                  <SessionChatHistoryDropdown
                    sessionId={sessionId}
                    currentChatId={currentChatId}
                    mode={mode}
                    onSelectChat={onSelectChat}
                    chatTitle={chatTitle}
                    isWorkEnabled={isWorkEnabled}
                  />
                ) : (
                  headerTitle.toLowerCase() !== 'new chat' && (
                    <TypographyP2Bold>{headerTitle}</TypographyP2Bold>
                  )
                )}
              </>
            )}
          </div>
        </LeftHeaderSpotlightWrapper>
        <div
          id="right-header-content"
          className={cn(
            'flex flex-row items-center',
            isOnChatRoute ? 'gap-1' : 'gap-2'
          )}
        >
          {showShareButton && (
            <ShareChatOnboardingTooltip
              enabled
              onShareClick={() => setIsShareModalOpen(true)}
            >
              <ShareChatButton onClick={() => setIsShareModalOpen(true)} />
            </ShareChatOnboardingTooltip>
          )}
          {isOnChatRoute && !isSidebarOpen && !isMobile && (
            <ChatOverflowMenu
              showSaveButton={!!showSaveButton}
              handleSaveToggle={handleSaveToggle}
              isChatSaved={isChatSaved}
              currentChatId={currentChatId}
              handleNewChat={handleNewChat}
              onDelete={showDeleteButton ? handleDeleteClick : undefined}
            />
          )}
          {showTrialDaysCountdownButton && (
            <EvidenceUpgradeCtaButton />
          )}
          {showUserDocsButton && (
            <CustomizeEvidenceButton
              hasEvidencePro={hasEvidencePro}
              onClick={handleCustomizeEvidenceClick}
            />
          )}
          {!isOnChatRoute && (
            <NonChatRouteActions
              onNewChat={onNewChat ?? handleNewChat}
              onClose={handleClose}
              isNewChat={isChatEmpty}
              currentChatId={currentChatId}
              sessionEvidenceExpandLayout={isOnSessionRoute}
            />
          )}
        </div>
        <ChatHeaderModals
          isAuthenticated={isAuthenticated}
          showSignUpModal={showSignUpModal}
          setShowSignUpModal={setShowSignUpModal}
          showUserDocsButton={!!showUserDocsButton}
          userDocsModalOpen={userDocsModalOpen}
          setUserDocsModalOpen={setUserDocsModalOpen}
          showShareButton={!!showShareButton}
          currentChatId={currentChatId}
          isShareModalOpen={isShareModalOpen}
          setIsShareModalOpen={setIsShareModalOpen}
          chatTitle={chatTitle}
          isStreaming={isStreaming}
          isDeleteDialogOpen={isDeleteDialogOpen}
          isDeletingChat={isDeletingChat}
          onCancelDelete={handleCancelDelete}
          onConfirmDelete={handleConfirmDelete}
        />
      </div>
      {!isOnChatRoute ? (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 -bottom-3 h-3 bg-gradient-to-b',
            IS_INSET_DESIGN_ENABLED()
              ? 'from-surface-shell to-surface-shell/0'
              : 'from-surface-container to-surface-container/0'
          )}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
};
